/* people: the People screens and the client edit form (W3, 2026-09-30).
 *
 * Spread into the main catalogue, so every top-level key here is a namespace
 * of its own: `people` (the list), `person` (one account), `editClient`.
 * Words the product already has (`common.*`, `nav.*`) are reused, not
 * repeated. */
export default {
  people: {
    title: 'People',
    subtitle: 'Everyone who can sign in to the firm’s workspace, and what each of them can see.',
    add: 'Add a person',
    back: 'Back to people',
    emptyTitle: 'Nobody here yet',
    empty: 'Add a colleague, or someone at a client who should follow their cases.',
    group: {
      firm_main: 'Main account',
      firm_manager: 'Lawyers at the firm',
      client_viewer: 'People at clients',
    },
    groupSub: {
      firm_main: 'Sees and changes everything, and is the only one who can add people.',
      firm_manager: 'Work on the clients and trademarks they are given.',
      client_viewer: 'Read the open cases of their own company. They never see matches.',
    },
  },

  person: {
    newTitle: 'Add a person',
    newSub:
      'Choose who they are and what they can see. You decide separately whether to e-mail them now.',
    kind: {
      firm_main: 'Main account',
      firm_manager: 'Lawyer at the firm',
      client_viewer: 'Someone at a client',
    },
    kindHint: {
      firm_manager:
        'Can work on the clients and trademarks you give them: matches, cases, documents.',
      client_viewer:
        'Can only read the open cases of the companies you give them. Cannot change anything.',
    },
    role: {
      senior_partner: 'Senior partner',
      partner: 'Partner',
      senior_associate: 'Senior associate',
      counsel: 'Counsel',
      associate: 'Associate',
    },
    status: {
      off: 'Switched off',
      notInvited: 'Not invited yet',
      invited: 'Invited {date}',
      expired: 'Invitation expired',
    },
    form: {
      title: 'Who they are',
      sub: 'Their name as colleagues will see it, and the language their e-mails are written in.',
      kind: 'They are',
      name: 'Full name',
      nameRequired: 'Enter their name',
      email: 'E-mail address',
      emailInvalid: 'Enter a valid e-mail address',
      emailHint: 'They sign in with this address. It cannot be changed later.',
      role: 'Role at the firm',
      noRole: 'Not given',
      language: 'Language',
      languageDefault: 'Not chosen (English)',
      languageHint: 'For their e-mails. They can change it themselves after signing in.',
    },
    access: {
      title: 'What they can see',
      sub: {
        firm_manager:
          'Whole companies, or only some of a company’s trademarks. Nothing else is visible to them.',
        client_viewer: 'The companies whose open cases they can read. At least one is needed.',
      },
      whole: 'The whole company',
      some: 'Only some trademarks',
      howMuch: 'How much of this company',
      noMarks: 'This company has no trademarks yet.',
      chooseMarks: 'Choose at least one trademark, or give the whole company.',
      search: 'Find a client company to add…',
      dropPending: 'Do not add {name}',
      viewerNeedsOne: 'Add at least one company: without one they would see nothing.',
      give: 'Give this access',
      given: 'Access given',
      remove: 'Remove',
      removeTitle: 'Remove access to {what}?',
      removeBody: 'They stop seeing it straight away. Nothing else is changed.',
      noneYet: 'They can see nothing yet. Add a company below.',
      mainSeesAll: 'The main account sees the whole firm. There is nothing to choose.',
      wholeFirm: 'The whole firm',
      none: 'Nothing yet',
      clients: '{n} company | {n} companies',
      someMarks: '{name}: {n} trademark | {name}: {n} trademarks',
    },
    invite: {
      title: 'Invitation',
      sub: 'An e-mail with a link to choose a password. The link works once, for 7 days.',
      now: 'E-mail them an invitation now',
      send: 'Send invitation',
      again: 'Send a new invitation',
      confirmTitle: 'E-mail an invitation to {email}?',
      body: 'They get a link to choose a password and sign in.',
      replaces: 'They get a new link to choose a password. The link sent earlier stops working.',
      sent: 'Sent to {email}.',
      done: 'Invitation sent',
      failed: 'The invitation was not sent',
      hasPassword:
        'They have a password and can sign in. A new invitation lets them choose another one.',
      waiting: 'Invited {sent}. The link works until {until}.',
      lapsed: 'Invited {sent}, but the link has expired unused.',
      never: 'Not invited yet: they cannot sign in until they are.',
    },
    account: {
      title: 'Their account',
      sub: 'Switching off keeps everything they did; deleting removes the account itself.',
    },
    off: {
      action: 'Switch off',
      undo: 'Switch back on',
      confirmTitle: 'Switch off {name}?',
      body: 'They are signed out and cannot sign in until you switch them back on. What they did stays.',
    },
    delete: {
      action: 'Delete',
      confirmTitle: 'Delete {name}?',
      body: 'The account and its access are removed and cannot be restored. Cases assigned to them become unassigned; the record of what they did stays.',
      done: '{name} deleted',
    },
    create: 'Add person',
    save: 'Save',
    saved: 'Saved',
    created: 'Person added',
    createdSub: 'No e-mail was sent. Send the invitation when you are ready.',
    createdNoInvite: 'Person added, but the invitation was not sent',
    failed: 'That did not work',
    emailTaken: 'Someone already has an account with this e-mail address.',
    summary: 'In short',
  },

  editClient: {
    crumb: 'Edit',
    title: 'Edit client',
    subtitle: 'Change what is stored about {name}. Only the fields you change are saved.',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Changes saved',
    failed: 'The changes were not saved',
    notSaved: 'Nothing is saved until you press Save changes.',
    delete: {
      title: 'Delete this client',
      sub: 'Removes the company with its {n} trademark, their documents and matches, and everyone’s access to it. | Removes the company with its {n} trademarks, their documents and matches, and everyone’s access to it.',
      action: 'Delete client',
      confirmTitle: 'Delete {name}?',
      body: 'Its {n} trademark, their documents and matches, and everyone’s access to the company are removed. This cannot be undone. | Its {n} trademarks, their documents and matches, and everyone’s access to the company are removed. This cannot be undone.',
      done: '{name} deleted',
      failed: 'The client was not deleted',
      hasCases: 'It has opposition cases. Delete those first, on the Oppositions page.',
    },
  },
}
